import { ThemeProvider } from '../theme/ThemeContext';
import { BrandingApplier } from '../theme/BrandingApplier';
import { MeProvider, useMe } from '../auth/MeContext';
import { I18nProvider } from '../I18nContext';
import {
  type LangCode,
  type PlatformUiI18nPayload,
} from '../i18n';
import { Sidebar } from '../dashboard/Sidebar';
import { Rail } from '../dashboard/Rail';
import { ResponseLengthProvider } from '../dashboard/ResponseLengthContext';
import { AskFiltersProvider } from '../dashboard/AskFiltersContext';
import { MessageStream } from '../chat/MessageStream';
import { SourcePanel } from '../chat/SourcePanel';
import { SourcePanelProvider } from '../chat/SourcePanelContext';
import { SendProvider } from '../chat/SendContext';
import { Composer } from '../chat/Composer';
import { ComposerStagingProvider } from '../chat/ComposerStagingContext';
import { AudioReplyProvider } from '../chat/AudioReplyContext';
import { AudioPlayer } from '../chat/AudioPlayer';
import { ImageLightboxProvider } from '../chat/ImageLightboxContext';
import { ImageLightbox } from '../chat/ImageLightbox';

/**
 * /dashboard - the chat product page.
 *
 * D5b scope: D5a + rail filters (document multi-select + language
 * picker) + History modal (list past threads, click to restore).
 * PR-D5c is the home stretch: Settings/Help modals + deletion of
 * the legacy dashboard.html and Jinja auth templates.
 *
 * Two providers wrap this page that the auth flow doesn't need:
 *   - ThemeProvider - light/dark toggle (auth shell is dark-locked)
 *   - MeProvider    - fetches /api/platform/me for user/client info
 */
export function Dashboard() {
  return (
    <ThemeProvider>
      <MeProvider>
        {/* I18nFromMeProvider reads the catalogue out of /me and
            exposes a bound translator via useT() to every
            descendant component. Sits inside MeProvider because
            the catalogue is part of the /me payload. */}
        <I18nFromMeProvider>
          <ResponseLengthProvider>
            {/* AskFiltersProvider must wrap SendProvider - SendProvider
                reads document_ids + language from filter state and
                merges them into every ask. */}
            <AskFiltersProvider>
              <AudioReplyProvider>
                <ComposerStagingProvider>
                  <SendProvider>
                    <SourcePanelProvider>
                      <ImageLightboxProvider>
                        <DashboardShell />
                        {/* Overlays - mounted once near the root so the
                            whole dashboard can trigger them via context
                            without prop-drilling. They render nothing
                            while idle. */}
                        <SourcePanel />
                        <ImageLightbox />
                        <AudioPlayer />
                      </ImageLightboxProvider>
                    </SourcePanelProvider>
                  </SendProvider>
                </ComposerStagingProvider>
              </AudioReplyProvider>
            </AskFiltersProvider>
          </ResponseLengthProvider>
        </I18nFromMeProvider>
      </MeProvider>
    </ThemeProvider>
  );
}


/**
 * Thin wrapper that turns /me's `platform_ui_i18n` payload into an
 * I18nProvider value. Renders descendants unconditionally, when
 * /me hasn't arrived yet (or omits the field on an older server),
 * the I18nProvider falls back to BUILTIN_EN so chrome strings still
 * render in English from the very first paint.
 *
 * The active language for the dashboard UI itself is derived from
 * the browser locale (navigator.language), pinning to the chat
 * reply-language picker would conflate two concerns (the user
 * might want chat in French but dashboard chrome in English).
 * Phase 4b may add an explicit picker.
 */
function I18nFromMeProvider({ children }: { children: import('react').ReactNode }) {
  const { me } = useMe();
  const lang = pickDashboardLang();
  // Server returns Record<string, Record<string, string>>; we cast
  // through PlatformUiI18nPayload (which is Partial<Record<LangCode, ...>>).
  // Unknown lang codes are tolerated by buildTranslator at lookup.
  const catalogue = (me?.platform_ui_i18n ?? {}) as PlatformUiI18nPayload;
  return (
    <I18nProvider lang={lang} catalogue={catalogue}>
      {children}
    </I18nProvider>
  );
}

/** Pick the dashboard UI language from the browser locale. Anything
 *  not in our supported set falls back to 'en'. */
function pickDashboardLang(): LangCode {
  const SUPPORTED: ReadonlySet<LangCode> = new Set<LangCode>([
    'en', 'es', 'fr', 'de', 'nl', 'it', 'pt', 'ja',
    'ko', 'zh', 'ar', 'hi', 'ru', 'pl', 'tr', 'sv',
    'th', 'vi',
  ]);
  if (typeof navigator === 'undefined') return 'en';
  const raw = (navigator.language || 'en').toLowerCase().split(/[-_]/)[0];
  return SUPPORTED.has(raw as LangCode) ? (raw as LangCode) : 'en';
}

function DashboardShell() {
  const { isLoading, error, me } = useMe();

  // Initial /me fetch hasn't returned yet. Showing a skeleton with
  // the shell's chrome (sidebar bg, rail bg) lets the user see
  // SOMETHING immediately rather than a blank page while we wait.
  if (isLoading) {
    return (
      <div className="dashboard-shell">
        <aside className="dashboard-sidebar dashboard-sidebar-loading" />
        <main className="dashboard-main">
          <div className="dashboard-rail dashboard-rail-loading" />
          <div className="dashboard-content" />
        </main>
      </div>
    );
  }

  // Non-401 error (401 → MeProvider already kicked us back to /login).
  // Render a minimal "couldn't load" surface so the user has something
  // to react to - refresh or contact admin.
  if (error) {
    return (
      <div className="dashboard-shell">
        <main className="dashboard-main">
          <div
            className="dashboard-content"
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexDirection: 'column',
              gap: 'var(--space-3)',
              padding: 'var(--space-8)',
              textAlign: 'center',
            }}
          >
            <h2 style={{ font: 'var(--font-display)' }}>
              Couldn&apos;t load your dashboard
            </h2>
            <p style={{ color: 'var(--text-dim)' }}>
              Please refresh the page. If this keeps happening, contact
              your administrator.
            </p>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="dashboard-shell">
      {/* Sets --accent, --font-display, etc. on <html> from the
          tenant's branding, loads any Google Fonts referenced, and
          unwinds on unmount. Sibling so it doesn't gate any layout. */}
      <BrandingApplier branding={me?.current_client?.branding ?? null} />
      <Sidebar />
      <main className="dashboard-main">
        <Rail />
        <div className="dashboard-content">
          <MessageStream />
        </div>
        <Composer />
      </main>
    </div>
  );
}
